'use client';

import * as React from 'react';
import { cn } from '../../lib/utils';
import { Avatar, type AvatarProps } from '../ui/avatar';

export interface UserProfileCardProps extends React.ComponentProps<'div'> {
  /** Display name shown on the first line. */
  name?: string;
  /** Sub-line — typically a company name, role, or email. */
  subtitle?: string;
  /** Optional second sub-line for tertiary info (e.g. mirrored company). */
  description?: React.ReactNode;
  /** Avatar image URL. */
  avatarSrc?: string;
  /** Fallback initials when no image is provided / available. */
  avatarFallback?: string;
  avatarSize?: AvatarProps['size'];
  /** Visual layout. Defaults to horizontal (avatar left, text right). */
  layout?: 'horizontal' | 'vertical';
  /** Optional adornment shown on the trailing edge (chevron, caret, etc.). */
  trailing?: React.ReactNode;
  /** Tone — controls text color. Useful when the card is on a dark sidebar. */
  tone?: 'inherit' | 'inverse';
}

export const UserProfileCard = React.forwardRef<HTMLDivElement, UserProfileCardProps>(
  (
    {
      name,
      subtitle,
      description,
      avatarSrc,
      avatarFallback,
      avatarSize = 'lg',
      layout = 'horizontal',
      trailing,
      tone = 'inherit',
      className,
      ...props
    },
    ref,
  ) => {
    const textColorClass =
      tone === 'inverse' ? 'text-accessible-text-color' : 'text-foreground';
    const subtleColorClass = tone === 'inverse' ? 'text-accessible-text-color/70' : 'text-muted-foreground';

    return (
      <div
        ref={ref}
        className={cn(
          layout === 'horizontal' ? 'flex items-center gap-3' : 'flex flex-col items-center gap-2 text-center',
          className,
        )}
        {...props}
      >
        <Avatar
          src={avatarSrc}
          fallback={avatarFallback}
          size={avatarSize}
          className={tone === 'inverse' ? 'bg-white/15 text-accessible-text-color' : undefined}
        />
        <div
          className={cn(
            layout === 'horizontal' ? 'min-w-0 flex-1' : 'min-w-0',
            'leading-tight',
          )}
        >
          {name && (
            <p className={cn('text-sm font-semibold truncate', textColorClass)}>{name}</p>
          )}
          {subtitle && (
            <p className={cn('text-xs truncate', subtleColorClass)}>{subtitle}</p>
          )}
          {description && (
            <div className={cn('text-xs mt-0.5', subtleColorClass)}>{description}</div>
          )}
        </div>
        {trailing && <div className='flex-shrink-0'>{trailing}</div>}
      </div>
    );
  },
);
UserProfileCard.displayName = 'UserProfileCard';
